import React, { useState, useEffect, useCallback } from "react";
import { ExpenseFormData, ExpenseCategory } from "../types/expense";

interface ExpenseFormProps {
  onAddExpense: (expense: ExpenseFormData) => Promise<void>;
  fixedCategory?: ExpenseCategory;
  clearTrigger?: number;
}

const ExpenseForm: React.FC<ExpenseFormProps> = ({
  onAddExpense,
  fixedCategory,
  clearTrigger,
}) => {
  // Load form data from localStorage on initial load
  const [formData, setFormData] = useState<ExpenseFormData>(() => {
    const savedFormData = localStorage.getItem("travel-expense-form");
    if (savedFormData) {
      const parsed = JSON.parse(savedFormData);
      return {
        ...parsed,
        category: fixedCategory || parsed.category, // Use fixed category if provided
        receipt: undefined, // File objects can't be restored from localStorage
      };
    }
    return {
      date: "",
      description: "",
      category: fixedCategory || ExpenseCategory.BELEGE,
      amount: "",
      location: "",
      duration: "",
      receipt: undefined,
      receiptMetadata: undefined,
    };
  });

  const [errors, setErrors] = useState<Partial<ExpenseFormData>>({});

  // Clear form data from localStorage
  const clearFormData = useCallback(() => {
    localStorage.removeItem("travel-expense-form");
    setFormData({
      date: "",
      description: "",
      category: fixedCategory || ExpenseCategory.BELEGE,
      amount: "",
      location: "",
      duration: "",
      receipt: undefined,
      receiptMetadata: undefined,
    });
  }, [fixedCategory]);

  // Update form data when fixedCategory changes
  useEffect(() => {
    if (fixedCategory && formData.category !== fixedCategory) {
      setFormData((prev) => ({
        ...prev,
        category: fixedCategory,
      }));
    }
  }, [fixedCategory, formData.category]);

  // Clear form data when clearTrigger changes
  useEffect(() => {
    if (clearTrigger && clearTrigger > 0) {
      clearFormData();
    }
  }, [clearTrigger, clearFormData]);

  // Save form data to localStorage whenever it changes (except receipt)
  useEffect(() => {
    const dataToSave = {
      ...formData,
      receipt: undefined, // Don't save File object
      receiptMetadata: formData.receipt
        ? {
            name: formData.receipt.name,
            size: formData.receipt.size,
            type: formData.receipt.type,
            lastModified: formData.receipt.lastModified,
          }
        : formData.receiptMetadata,
    };
    console.log("Saving form data to localStorage:", dataToSave);
    localStorage.setItem("travel-expense-form", JSON.stringify(dataToSave));
  }, [formData]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof ExpenseFormData]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const metadata = file
      ? {
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified,
        }
      : undefined;

    console.log("File selected:", { file: file?.name, metadata });

    setFormData((prev) => ({
      ...prev,
      receipt: file,
      receiptMetadata: metadata,
    }));
  };

  // Utility function to get file display info
  const getFileInfo = () => {
    console.log("Getting file info:", {
      hasReceipt: !!formData.receipt,
      hasMetadata: !!formData.receiptMetadata,
      metadata: formData.receiptMetadata,
    });

    if (formData.receipt) {
      return {
        name: formData.receipt.name,
        size: formData.receipt.size,
      };
    }
    if (formData.receiptMetadata) {
      return {
        name: formData.receiptMetadata.name,
        size: formData.receiptMetadata.size,
      };
    }
    return null;
  };

  const handleRemoveFile = () => {
    setFormData((prev) => ({
      ...prev,
      receipt: undefined,
      receiptMetadata: undefined,
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<ExpenseFormData> = {};

    // Description is required for both categories
    if (!formData.description.trim()) {
      newErrors.description = "Beschreibung ist erforderlich";
    }

    // Amount is required for both categories
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = "Betrag muss größer als 0 sein";
    }

    // Additional validation for Spesenabrechnung
    if (formData.category === ExpenseCategory.SPESENABRECHNUNG) {
      if (!formData.location.trim()) {
        newErrors.location = "Ort ist erforderlich";
      }

      if (!formData.date) {
        newErrors.date = "Datum ist erforderlich";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      try {
        await onAddExpense(formData);
        // Reset form but keep selected category
        setFormData((prev) => ({
          date: "",
          description: "",
          category: fixedCategory || prev.category, // Use fixed category or keep the previously selected category
        amount: "",
        location: "",
        duration: "",
        receipt: undefined,
        receiptMetadata: undefined,
      }));
      } catch (error) {
        console.error("Failed to add expense:", error);
        // Error is already handled in the parent component
      }
    }
  };

  const handleCategoryChange = (category: ExpenseCategory) => {
    if (!fixedCategory) {
      setFormData((prev) => ({
        ...prev,
        category,
      }));
      // Clear errors when switching categories
      setErrors({});
    }
  };

  // Custom File Upload Component
  const CustomFileUpload = ({
    onChange,
  }: {
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  }) => {
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const handleClick = () => {
      fileInputRef.current?.click();
    };

    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      const files = e.dataTransfer.files;
      if (files && files[0]) {
        const event = {
          target: {
            files: files,
          },
        } as any;
        onChange(event);
      }
    };

    return (
      <div className="custom-file-upload">
        <input
          ref={fileInputRef}
          type="file"
          onChange={onChange}
          accept=".pdf,.jpg,.jpeg,.png,.gif"
          className="hidden-file-input"
        />
        <div
          className="file-upload-area"
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <div className="file-upload-content">
            <i className="fas fa-cloud-upload-alt file-upload-icon"></i>
            <div className="file-upload-text">
              <span className="file-upload-main">
                Beleg auswählen oder hierher ziehen
              </span>
              <span className="file-upload-sub">
                PDF, JPG, PNG, GIF bis 10MB
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // File Preview Component
  const FilePreview = () => {
    const fileInfo = getFileInfo();

    if (!fileInfo) return null;

    return (
      <div className="file-preview">
        <i className="fas fa-file-alt file-preview-icon"></i>
        <div className="file-preview-details">
          <span className="file-name">{fileInfo.name}</span>
          <span className="file-size">
            ({(fileInfo.size / 1024).toFixed(1)} KB)
          </span>
        </div>
        <button
          type="button"
          className="remove-file-btn"
          onClick={handleRemoveFile}
        >
          <i className="fas fa-times"></i>
        </button>
      </div>
    );
  };

  return (
    <div className="expense-form">
      <h3>
        {fixedCategory === ExpenseCategory.BELEGE
          ? "Neue Belege hinzufügen"
          : "Neue Spesenabrechnung hinzufügen"}
      </h3>
      <form onSubmit={handleSubmit}>
        {/* Category Selection - only show if not fixed */}
        {!fixedCategory && (
          <div className="form-group">
            <label htmlFor="category">Kategorie</label>
            <div className="category-switch">
              <button
                type="button"
                className={`switch-option ${
                  formData.category === ExpenseCategory.BELEGE ? "active" : ""
                }`}
                onClick={() => handleCategoryChange(ExpenseCategory.BELEGE)}
              >
                Belege
              </button>
              <button
                type="button"
                className={`switch-option ${
                  formData.category === ExpenseCategory.SPESENABRECHNUNG
                    ? "active"
                    : ""
                }`}
                onClick={() =>
                  handleCategoryChange(ExpenseCategory.SPESENABRECHNUNG)
                }
              >
                Spesenabrechnung
              </button>
            </div>
          </div>
        )}

        {/* Info box for Spesenabrechnung category */}
        {formData.category === ExpenseCategory.SPESENABRECHNUNG && (
          <div className="info-box">
            <p>
              Anreise und Abreisetage werden mit jeweils 14 € berechnet
              abzüglich bezahlte Mahlzeiten falls vorhanden. Volle
              Abwesenheitstage werden mit 28 € pro Tag berechnet.
            </p>
          </div>
        )}

        {/* Description field - shown for both categories */}
        <div className="form-group">
          <label htmlFor="description">Beschreibung *</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Ausgabenbeschreibung eingeben..."
            className={errors.description ? "error" : ""}
            rows={3}
            required
          />
          {errors.description && (
            <span className="error-message">{errors.description}</span>
          )}
        </div>

        {/* Fields for Belege category */}
        {formData.category === ExpenseCategory.BELEGE && (
          <>
            <div className="form-group">
              <label htmlFor="amount">Betrag (EUR) *</label>
              <input
                type="number"
                id="amount"
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                placeholder="0.00"
                step="0.01"
                min="0"
                className={errors.amount ? "error" : ""}
                required
              />
              {errors.amount && (
                <span className="error-message">{errors.amount}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="receipt">Beleg anhängen</label>
              <CustomFileUpload onChange={handleFileChange} />
              <FilePreview />
            </div>
          </>
        )}

        {/* Fields for Spesenabrechnung category */}
        {formData.category === ExpenseCategory.SPESENABRECHNUNG && (
          <>
            <div className="form-group">
              <label htmlFor="date">Datum *</label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                className={errors.date ? "error" : ""}
                required
              />
              {errors.date && (
                <span className="error-message">{errors.date}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="location">Ort *</label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                placeholder="Ort eingeben..."
                className={errors.location ? "error" : ""}
                required
              />
              {errors.location && (
                <span className="error-message">{errors.location}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="duration">Dauer</label>
              <input
                type="text"
                id="duration"
                name="duration"
                value={formData.duration}
                onChange={handleInputChange}
                placeholder="z.B. 2 Stunden, 1 Tag, 3 Nächte..."
                className={errors.duration ? "error" : ""}
              />
              {errors.duration && (
                <span className="error-message">{errors.duration}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="amount">Betrag (EUR) *</label>
              <input
                type="number"
                id="amount"
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                placeholder="0.00"
                step="0.01"
                min="0"
                className={errors.amount ? "error" : ""}
                required
              />
              {errors.amount && (
                <span className="error-message">{errors.amount}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="receipt">Beleg anhängen</label>
              <CustomFileUpload onChange={handleFileChange} />
              <FilePreview />
            </div>
          </>
        )}

        <div className="form-actions">
          <button type="submit" className="submit-btn">
            Ausgabe hinzufügen
          </button>
          <button
            type="button"
            className="clear-form-btn icon-btn"
            onClick={clearFormData}
            title="Formular zurücksetzen - Alle Eingabefelder löschen"
          >
            <i className="fas fa-eraser"></i>
          </button>
        </div>
      </form>
    </div>
  );
};

export default ExpenseForm;
